// SF Symbols-inspired icon system
// Consistent semantic mapping for unified design

import {
  Accessibility,
  Activity,
  AlertCircle,
  AlertTriangle,
  // Additional commonly used
  Archive,
  ArrowDown,
  ArrowDownLeft,
  ArrowDownRight,
  ArrowLeft,
  ArrowLeftRight,
  ArrowRight,
  ArrowRightLeft,
  ArrowUp,
  ArrowUpDown,
  ArrowUpRight,
  Award,
  Badge,
  Banknote,
  BarChart,
  BarChart3,
  Bell,
  BellRing,
  Box,
  Bug,
  Building,
  Building2,
  Calculator,
  Calendar,
  Camera,
  Check,
  CheckCircle,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  // Navigation
  ChevronRight,
  ChevronsUpDown,
  ChevronUp,
  Circle,
  ClipboardCheck,
  // Status & Feedback
  Clock,
  Cloud,
  Code,
  Coins,
  Copy,
  CreditCard,
  Database,
  DollarSign,
  Download,
  ExternalLink,
  Eye,
  EyeOff,
  FileCheck,
  FileText,
  Filter,
  // Design & UI (CanonPlayground)
  Fingerprint,
  FlaskConical,
  // Technology & Network
  Globe,
  Globe2,
  Heart,
  HelpCircle,
  Home,
  // Empty States (PRD-ADM-UX-2026)
  Inbox,
  Info,
  Landmark,
  Layers,
  Layout,
  // Navigation
  LayoutDashboard,
  LayoutGrid,
  ListChecks,
  Loader2,
  Lock,
  LogOut,
  Mail,
  MapPin,
  Menu,
  MessageCircle,
  Minus,
  Monitor,
  Moon,
  Network,
  Palette,
  PartyPopper,
  Pencil,
  Play,
  Plus,
  Receipt,
  RefreshCw,
  Rocket,
  RotateCw,
  // Financial & Crypto
  Route,
  // UI Elements
  Search,
  // Actions & Flow
  Send,
  Server,
  Settings,
  // Authentication & Security
  Shield,
  ShieldCheck,
  Smartphone,
  Sparkles,
  Star,
  Sun,
  // Actions
  Terminal,
  Timer,
  Trash2,
  TrendingDown,
  TrendingUp,
  Type,
  Unlock,
  Upload,
  User,
  UserCheck,
  Users,
  Wallet,
  Wallet2,
  Webhook,
  Wifi,
  WifiOff,
  Wrench,
  X,
  XCircle,
  Zap
} from 'lucide-react';

// Semantic icon mapping for consistent usage across the app
export const sfIcons = {
  // Core actions
  verify: ShieldCheck,
  createAddress: Wallet,
  createAddress2: Wallet2,
  receiveFiat: Banknote,

  // Financial concepts
  crypto: Circle,
  fiat: DollarSign,
  bank: Building,
  card: CreditCard,
  exchange: ArrowRightLeft,

  // User concepts  
  user: User,
  userCheck: UserCheck,
  users: Users,
  route: Route,

  // Technology & Features
  security: Shield,
  speed: Clock,
  global: Globe,
  global2: Globe2,
  network: Network,
  mobile: Smartphone,
  wifi: Wifi,
  wifiOff: WifiOff,

  // Status & Actions
  success: CheckCircle,
  pending: Clock,
  warning: AlertCircle,
  error: XCircle,
  info: Info,
  help: HelpCircle,
  send: Send,

  // Actions (NEW)
  code: Code,
  delete: Trash2,
  edit: Pencil,
  download: Download,
  upload: Upload,
  refresh: RefreshCw,
  rotateCw: RotateCw,
  settings: Settings,
  flask: FlaskConical,
  add: Plus,
  remove: Minus,
  copy: Copy,

  // Navigation & UI
  next: ArrowRight,
  back: ArrowLeft,
  up: ArrowUp,
  down: ArrowDown,
  chevronRight: ChevronRight,
  chevronLeft: ChevronLeft,
  chevronUp: ChevronUp,
  chevronDown: ChevronDown,
  menu: Menu,
  close: X,
  check: Check,

  // UI Elements (NEW)
  search: Search,
  filter: Filter,
  show: Eye,
  hide: EyeOff,
  lock: Lock,
  unlock: Unlock,
  externalLink: ExternalLink,

  // Empty States (NEW - PRD-ADM-UX-2026)
  noData: Inbox,
  maintenance: Wrench,

  // Navigation (NEW)
  dashboard: LayoutDashboard,
  transactions: Receipt,
  logout: LogOut,
  notifications: Bell,
  home: Home,
  landmark: Landmark,
  listChecks: ListChecks,

  // Design & UI (CanonPlayground & Design System)
  fingerprint: Fingerprint,
  award: Award,
  palette: Palette,
  layers: Layers,
  typography: Type,
  activity: Activity,
  box: Box,
  accessibility: Accessibility,
  coins: Coins,
  mail: Mail,
  layout: Layout,
  layoutGrid: LayoutGrid,
  cloud: Cloud,
  sparkles: Sparkles,
  loader: Loader2,
  loading: Loader2, // Alias for loader (used in ChainSelectionCard, FiatSelectionCard)
  alertTriangle: AlertTriangle,
  building2: Building2,
  calculator: Calculator,
  camera: Camera,
  mapPin: MapPin,
  banknote: Banknote, // Alias for payment rails
  globe: Globe, // Alias for payment rails
  wallet: Wallet, // Alias for wallet icon
  clock: Clock, // Alias for clock icon

  // Additional commonly used icons
  archive: Archive,
  arrowDownLeft: ArrowDownLeft,
  arrowDownRight: ArrowDownRight,
  arrowLeftRight: ArrowLeftRight,
  arrowUpDown: ArrowUpDown,
  arrowUpRight: ArrowUpRight,
  badge: Badge,
  barChart: BarChart,
  barChart3: BarChart3,
  bellRing: BellRing,
  bug: Bug,
  calendar: Calendar,
  checkCircle2: CheckCircle2,
  chevronsUpDown: ChevronsUpDown,
  clipboardCheck: ClipboardCheck,
  fileCheck: FileCheck,
  fileText: FileText,
  heart: Heart,
  monitor: Monitor,
  messageCircle: MessageCircle,
  partyPopper: PartyPopper,
  play: Play,
  server: Server,
  database: Database,
  terminal: Terminal,
  timer: Timer,
  trendingUp: TrendingUp,
  trendingDown: TrendingDown,
  moon: Moon,
  sun: Sun,
  webhook: Webhook,
  zap: Zap,

  // Onboarding & Premium States (EmptyState variants)
  rocket: Rocket,
  star: Star,

  // ============================================
  // PRODUCT FEATURES (P1 Landing Blocks)
  // Semantic mappings for core product features
  // ============================================
  // Product Features
  liquidationAddress: Wallet,        // Crypto address for auto-conversion
  virtualAccount: Building,          // Bank account details (IBAN/ACH/PIX)
  payoutRoutes: Route,               // Payment routing configuration (already exists as 'route')
  productWallet: Wallet2,           // Multi-currency wallet

  // Security & Compliance Features
  zeroTrust: Lock,                   // Zero-Trust architecture
  e2eEncryption: Lock,               // End-to-End Encryption (same icon as zeroTrust)
  rowLevelSecurity: Database,        // Row-Level Security (RLS)
  kycAml: UserCheck,                 // KYC/AML compliance

  // Coverage & Infrastructure
  coverageCountries: Globe,          // Country coverage
  coverageCurrencies: Coins,         // Currency support
  coverageNetworks: Network,         // Blockchain networks

  // ============================================
  // ALIASES (React Error #130 Prevention)
  // These are alternative names for the same icons.
  // Some code uses direct lucide-react names (e.g., `sfIcons.plus`)
  // instead of semantic names (e.g., `sfIcons.add`).
  // Adding aliases prevents undefined icon errors.
  // @see REACT_130_310_SWEEP.md
  // ============================================
  alertCircle: AlertCircle,    // alias for warning
  arrowRight: ArrowRight,      // alias for next
  checkCircle: CheckCircle,    // alias for success
  creditCard: CreditCard,      // alias for card
  dollarSign: DollarSign,      // alias for fiat
  eye: Eye,                    // alias for show
  eyeOff: EyeOff,              // alias for hide
  globe2: Globe2,              // alias for global2
  plus: Plus,                  // alias for add
  shield: Shield,              // alias for security
  shieldCheck: ShieldCheck,    // alias for verify
  smartphone: Smartphone,      // alias for mobile
  trash: Trash2,               // alias for delete
  wallet2: Wallet2,            // alias for createAddress2
} as const;

export type SFIconName = keyof typeof sfIcons;

/**
 * Icon categories for better developer experience
 * Helps discover icons by semantic category
 */
export const iconCategories = {
  actions: [
    'verify',
    'createAddress',
    'receiveFiat',
    'send',
    'delete',
    'edit',
    'download',
    'upload',
    'refresh',
    'add',
    'remove',
    'copy',
  ] as const,
  financial: [
    'crypto',
    'fiat',
    'bank',
    'card',
    'exchange',
  ] as const,
  status: [
    'success',
    'pending',
    'warning',
    'error',
    'info',
    'help',
  ] as const,
  navigation: [
    'next',
    'back',
    'up',
    'down',
    'chevronRight',
    'chevronLeft',
    'chevronUp',
    'chevronDown',
    'menu',
    'close',
    'dashboard',
    'transactions',
    'logout',
    'notifications',
  ] as const,
  ui: [
    'search',
    'filter',
    'show',
    'hide',
    'lock',
    'unlock',
    'externalLink',
    'settings',
    'loader',
    'loading',
    'mail',
    'cloud',
    'sparkles',
  ] as const,
  design: [
    'fingerprint',
    'award',
    'palette',
    'layers',
    'typography',
    'activity',
    'box',
    'accessibility',
    'layout',
  ] as const,
  data: [
    'barChart',
    'trendingUp',
    'trendingDown',
    'fileText',
    'archive',
  ] as const,
  system: [
    'server',
    'database',
    'monitor',
    'bug',
    'loader',
  ] as const,
  empty: [
    'noData',
    'maintenance',
  ] as const,
  user: [
    'user',
    'users',
  ] as const,
  technology: [
    'security',
    'speed',
    'global',
    'network',
    'mobile',
    'wifi',
    'wifiOff',
  ] as const,
  product: [
    'liquidationAddress',
    'virtualAccount',
    'payoutRoutes',
    'productWallet',
  ] as const,
  securityFeatures: [
    'zeroTrust',
    'e2eEncryption',
    'rowLevelSecurity',
    'kycAml',
  ] as const,
  coverage: [
    'coverageCountries',
    'coverageCurrencies',
    'coverageNetworks',
  ] as const,
} as const;

/**
 * Get all icon names in a category
 * @example
 * getIconsByCategory('actions') // ['verify', 'createAddress', ...]
 */
export function getIconsByCategory(category: keyof typeof iconCategories): readonly SFIconName[] {
  return iconCategories[category];
}

/**
 * Get all available icon names
 */
export function getAllIconNames(): SFIconName[] {
  return Object.keys(sfIcons) as SFIconName[];
}